import { isComplexObject } from "./guardComplexObject.js";
import { throwEmpty } from "./guardEmpty.js";
import { ClassInstance } from "./guardInstance.js";
import { throwNull } from "./guardNull.js";
import { throwNotType } from "./guardType.js";
import { throwUndefined } from "./guardUndefined.js";

/**
 * Throws an error if the provided value is undefined or null.
 * 
 * @param guardName - The name of the variable being guarded.
 * @param guard - The value to check.
 * @throws {Error} Throws an error if `guard` is `undefined` or `null`.
 */
export const throwUndefinedOrNull = (guardName: string, guard: any): void => {
  throwUndefined(guardName, guard);
  throwNull(guardName, guard);
}

/**
 * Throws an error if the provided value is not a string or is an empty string.
 * 
 * @param guardName - The name of the variable being guarded.
 * @param guard - The value to check.
 * @throws {Error} Throws an error if `guard` is not a string or if it's empty ('').
 */
export const throwNotStringOrEmpty = (guardName: string, guard: any): void => {
  throwNotType(guardName, guard, 'string')
  throwEmpty(guardName, guard);
}

/**
 * Generates an error message when a value is not an instance of the specified class or a complex object.
 * 
 * @param guardName - The name of the variable being guarded.
 * @param guardType - The expected class constructor.
 * @returns A formatted error message.
 */
export const notInstanceOrObjectMessage = (guardName: string, guardType: ClassInstance): string => (
  `'${guardName}' must be an instance of '${guardType.name}' or a complex object`
)

/**
 * Throws an error if the provided value is not an instance of the specified class or a complex object.
 * 
 * @param guardName - The name of the variable being guarded.
 * @param guard - The value to check.
 * @param guardType - The expected class constructor.
 * @throws {Error} Throws an error if `guard` is not a complex object and not an instance of `guardType`.
 */
export const throwNotInstanceOrObject = (guardName: string, guard: any, guardType: ClassInstance): void => {
  if (isComplexObject(guard)) return;
  const isInstance = guard instanceof guardType;
  if (isInstance === false) {
    throw new Error(notInstanceOrObjectMessage(guardName, guardType));
  }
}
